import { useState, useEffect } from "react";
import { useAuthors, useCreateAuthor } from "@/hooks/useAuthors";
import { Author } from "@/types/authors";
import { Button } from "@/components/ui";

const inputClass = "w-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all";

interface AuthorSelectorProps {
  value: string | null;
  onChange: (author: Author | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const AuthorSelector = ({ value, onChange, placeholder = "Select an author", disabled }: AuthorSelectorProps) => {
  const { data: authors, isLoading } = useAuthors();
  const createMutation = useCreateAuthor();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTitle, setNewTitle] = useState("");

  const selectedAuthor = authors?.find((a) => a.authorId === value) || null;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-author-selector]")) {
        setIsOpen(false);
        setIsCreating(false);
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (author: Author | null) => {
    onChange(author);
    setIsOpen(false);
  };

  const handleCreateNew = async () => {
    if (!newName.trim()) return;

    try {
      const author = await createMutation.mutateAsync({
        displayName: newName.trim(),
        title: newTitle.trim() || null,
      });
      onChange(author);
      setIsCreating(false);
      setIsOpen(false);
      setNewName("");
      setNewTitle("");
    } catch (error) {
      console.error("Failed to create author:", error);
    }
  };

  return (
    <div className="relative" data-author-selector>
      {/* Selected value display */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`${inputClass} text-left flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {selectedAuthor ? (
          <>
            {selectedAuthor.avatarUrl ? (
              <img
                src={selectedAuthor.avatarUrl}
                alt=""
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-semibold">
                {selectedAuthor.displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="block truncate font-medium">{selectedAuthor.displayName}</span>
              {selectedAuthor.title && (
                <span className="block truncate text-xs text-slate-500">{selectedAuthor.title}</span>
              )}
            </div>
          </>
        ) : (
          <span className="text-slate-400">{isLoading ? "Loading..." : placeholder}</span>
        )}
        <svg className="w-4 h-4 text-slate-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 max-h-80 overflow-hidden">
          {isCreating ? (
            <div className="p-4 space-y-3">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Create new author</p>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Display name"
                className={inputClass}
                autoFocus
              />
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Title (optional)"
                className={inputClass}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsCreating(false);
                    setNewName("");
                    setNewTitle("");
                  }}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCreateNew}
                  loading={createMutation.isPending}
                  disabled={!newName.trim()}
                >
                  Create
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="max-h-60 overflow-y-auto">
                {/* Clear selection option */}
                {selectedAuthor && (
                  <button
                    type="button"
                    onClick={() => handleSelect(null)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 text-sm text-slate-500 border-b border-slate-100 dark:border-slate-800"
                  >
                    Clear selection
                  </button>
                )}

                {/* Author list */}
                {authors?.map((author) => (
                  <button
                    key={author.authorId}
                    type="button"
                    onClick={() => handleSelect(author)}
                    className={`w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors ${
                      author.authorId === value ? "bg-primary/5" : ""
                    }`}
                  >
                    {author.avatarUrl ? (
                      <img
                        src={author.avatarUrl}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                        {author.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="block truncate font-medium text-slate-900 dark:text-white">
                        {author.displayName}
                      </span>
                      {author.title && (
                        <span className="block truncate text-xs text-slate-500">{author.title}</span>
                      )}
                    </div>
                    {author.authorId === value && (
                      <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}

                {!authors?.length && !isLoading && (
                  <div className="px-4 py-6 text-center text-sm text-slate-500">
                    No authors yet
                  </div>
                )}
              </div>

              {/* Create new option */}
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 border-t border-slate-100 dark:border-slate-800 text-primary font-medium"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                Create new author
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AuthorSelector;
